import { MailOutlined, MessageOutlined } from "@ant-design/icons"
import { Avatar, Modal } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import {
  OVERVIEW_ACCENTS,
  type OverviewAccent
} from "../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import { initialsOf } from "../../Lead/LeadWorkspacePage/overview/overview-initials.ts"
import { accentForCustomer } from "../../CustomersPage/components/CustomersTable/customer-avatar-accent.ts"

export type Tone =
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "processing"
  | "neutral"

const TONE_DOT: Record<Tone, string> = {
  success: "bg-green-600",
  warning: "bg-amber-500",
  danger: "bg-red-600",
  info: "bg-sky-600",
  processing: "bg-[#2A7D8C]",
  neutral: "bg-slate-400"
}

const TONE_PILL: Record<Tone, string> = {
  success: "bg-green-50 text-green-800",
  warning: "bg-amber-50 text-amber-800",
  danger: "bg-red-50 text-red-700",
  info: "bg-sky-50 text-sky-800",
  processing:
    "bg-[var(--dk-selected,#E7F3F1)] text-[var(--dk-primary-text,#1A5560)]",
  neutral: "bg-slate-100 text-slate-700"
}

/** A status is a dot plus its words; colour is never the only signal. */
export const StatusDot = ({
  tone,
  label,
  pill = false
}: {
  tone: Tone
  label: ReactNode
  pill?: boolean
}) => (
  <span
    className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] ${
      pill ? `rounded-full px-2 py-0.5 font-medium ${TONE_PILL[tone]}` : ""
    }`}
  >
    <span
      aria-hidden
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${TONE_DOT[tone]}`}
    />
    {label}
  </span>
)

export const ContactAvatar = ({
  name,
  seed,
  size = 36
}: {
  name: string
  seed?: string
  size?: number
}) => {
  const accent = OVERVIEW_ACCENTS[accentForCustomer(seed || name || "?")]
  return (
    <Avatar
      aria-hidden
      size={size}
      className="shrink-0 font-semibold"
      style={{ backgroundColor: accent.background, color: accent.color }}
    >
      {initialsOf(name) ?? "?"}
    </Avatar>
  )
}

export const IconBubble = ({
  icon,
  accent = "teal",
  size = 40
}: {
  icon: ReactNode
  accent?: OverviewAccent | "red" | "amber"
  size?: number
}) => {
  const palette =
    accent === "red"
      ? { color: "#DC2626", background: "var(--dk-error-bg, #FEF2F2)" }
      : accent === "amber"
        ? { color: "#D97706", background: "var(--dk-warning-bg, #FFF7ED)" }
        : OVERVIEW_ACCENTS[accent]
  return (
    <span
      aria-hidden
      className="inline-flex shrink-0 items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        color: palette.color,
        backgroundColor: palette.background,
        fontSize: size * 0.45
      }}
    >
      {icon}
    </span>
  )
}

export const ChannelLabel = ({
  channel,
  iconOnly = false
}: {
  channel: string
  iconOnly?: boolean
}) => {
  const { t } = useTranslation()
  const label = t(`commHub.channel.${channel}`, { defaultValue: channel })
  const icon =
    channel === "sms" ? (
      <MessageOutlined aria-hidden />
    ) : (
      <MailOutlined aria-hidden />
    )
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-slate-700">
      {icon}
      {iconOnly ? <span className="sr-only">{label}</span> : label}
    </span>
  )
}

export type MessageSource = "manual" | "campaign" | "automation" | "system"

const SOURCE_STYLE: Record<MessageSource, string> = {
  manual: "bg-slate-100 text-slate-700",
  campaign:
    "bg-[var(--dk-selected,#E7F3F1)] text-[var(--dk-primary-text,#1A5560)]",
  automation: "bg-indigo-50 text-indigo-700",
  system: "bg-amber-50 text-amber-800"
}

export const SourceTag = ({ source }: { source?: string | null }) => {
  const { t } = useTranslation()
  if (!source) return null
  const style =
    SOURCE_STYLE[source as MessageSource] ?? "bg-slate-100 text-slate-700"
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ${style}`}
    >
      {t(`commHub.source.${source}`, { defaultValue: source })}
    </span>
  )
}

export const SectionCard = ({
  title,
  extra,
  children,
  className = "",
  bodyClassName = "",
  headingLevel = 2
}: {
  title?: ReactNode
  extra?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
  headingLevel?: 2 | 3
}) => {
  const Heading = headingLevel === 2 ? "h2" : "h3"
  return (
    <section
      className={`rounded-lg border border-solid border-slate-200 bg-surface ${className}`}
    >
      {(title || extra) && (
        <div className="flex items-center justify-between gap-3 border-0 border-b border-solid border-slate-100 px-5 py-3">
          {title && (
            <Heading className="m-0 text-base font-semibold text-slate-900">
              {title}
            </Heading>
          )}
          {extra}
        </div>
      )}
      <div className={bodyClassName || "px-5 py-3"}>{children}</div>
    </section>
  )
}

export interface WizardStep {
  key: string
  label: string
}

/** Numbered step indicator shared by every Communication wizard. */
export const StepIndicator = ({
  steps,
  current,
  onSelect,
  canSelect
}: {
  steps: WizardStep[]
  current: number
  onSelect?: (index: number) => void
  canSelect?: (index: number) => boolean
}) => {
  const { t } = useTranslation()
  return (
    <ol
      aria-label={t("commHub.wizard.progress")}
      className="m-0 flex list-none items-center gap-2 p-0"
    >
      {steps.map((step, index) => {
        const done = index < current
        const active = index === current
        const selectable = !!onSelect && (canSelect?.(index) ?? index < current)
        const marker = (
          <span
            className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold ${
              active || done
                ? "bg-[#2A7D8C] text-white"
                : "border border-solid border-slate-300 bg-surface text-slate-500"
            }`}
          >
            {done ? "✓" : index + 1}
          </span>
        )
        const text = (
          <span
            className={`whitespace-nowrap text-sm ${
              active
                ? "font-semibold text-[var(--dk-primary-text,#1A5560)]"
                : "text-slate-600"
            }`}
          >
            {step.label}
          </span>
        )
        return (
          <li
            key={step.key}
            className="flex min-w-0 flex-1 items-center gap-2"
            aria-current={active ? "step" : undefined}
          >
            {selectable ? (
              <button
                type="button"
                onClick={() => onSelect(index)}
                className="flex cursor-pointer items-center gap-2 rounded border-0 bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2A7D8C]"
              >
                {marker}
                {text}
              </button>
            ) : (
              <span className="flex items-center gap-2">
                {marker}
                {text}
              </span>
            )}
            {index < steps.length - 1 && (
              <span
                aria-hidden
                className={`hidden h-px flex-1 sm:block ${
                  done ? "bg-[#2A7D8C]" : "bg-slate-200"
                }`}
              />
            )}
          </li>
        )
      })}
    </ol>
  )
}

/**
 * The one modal frame for composer, campaign, automation and template editors:
 * header (title, optional subtitle and steps), scrolling body, footer with
 * secondary actions on the left and the primary on the right.
 */
export const HubModal = ({
  open,
  title,
  subtitle,
  steps,
  onCancel,
  footerLeft,
  footerRight,
  width = 1080,
  children,
  closable = true,
  afterOpenChange,
  labelledBy
}: {
  open: boolean
  title: ReactNode
  subtitle?: ReactNode
  steps?: ReactNode
  onCancel: () => void
  footerLeft?: ReactNode
  footerRight?: ReactNode
  width?: number
  children: ReactNode
  closable?: boolean
  afterOpenChange?: (open: boolean) => void
  labelledBy?: string
}) => (
  <Modal
    open={open}
    onCancel={onCancel}
    closable={closable}
    keyboard={closable}
    maskClosable={false}
    width={width}
    centered
    destroyOnHidden
    afterOpenChange={afterOpenChange}
    aria-labelledby={labelledBy}
    styles={{
      body: { padding: 0 },
      content: { padding: 0, overflow: "hidden" },
      header: { margin: 0 }
    }}
    title={
      <div className="flex flex-col gap-3 border-0 border-b border-solid border-slate-100 px-6 pb-4 pt-5 text-left">
        <div>
          <div id={labelledBy} className="text-xl font-semibold text-slate-900">
            {title}
          </div>
          {subtitle && (
            <div className="mt-0.5 text-sm font-normal text-slate-600">
              {subtitle}
            </div>
          )}
        </div>
        {steps}
      </div>
    }
    footer={
      footerLeft || footerRight ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-0 border-t border-solid border-slate-100 px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">{footerLeft}</div>
          <div className="flex flex-wrap items-center gap-2">{footerRight}</div>
        </div>
      ) : null
    }
  >
    <div className="max-h-[calc(100vh-260px)] overflow-y-auto [&_:is(h1,h2,h3)]:text-left">
      {children}
    </div>
  </Modal>
)

export const Kpi = ({
  label,
  value,
  hint,
  icon,
  accent,
  onClick,
  ariaLabel
}: {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  icon: ReactNode
  accent?: OverviewAccent | "red" | "amber"
  onClick?: () => void
  ariaLabel?: string
}) => {
  const body = (
    <>
      <IconBubble icon={icon} accent={accent} size={48} />
      <span className="flex min-w-0 flex-col items-start">
        <span className="text-sm text-slate-600">{label}</span>
        <span className="text-[28px] font-semibold leading-tight text-slate-900">
          {value}
        </span>
        {hint && <span className="text-xs text-slate-500">{hint}</span>}
      </span>
    </>
  )
  const className =
    "flex w-full items-center gap-4 rounded-lg border border-solid border-slate-200 bg-surface px-5 py-4 text-left"
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={`${className} cursor-pointer transition-shadow hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2A7D8C]`}
    >
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  )
}
